"use client";

import * as React from "react";
import { useMergeRefs } from "@/hooks/ui/useMergeRefs";
import { cn } from "@/lib/utils";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cva, type VariantProps } from "class-variance-authority";
import { ChevronLeft, ChevronRight } from "lucide-react";

const Tabs = TabsPrimitive.Root;

const tabsListVariants = cva("inline-flex items-center", {
  variants: {
    variant: {
      default: "justify-center h-10 rounded-md bg-cede-bg-component p-1 text-cede-text-secondary",
      segmented:
        "grid w-full grid-cols-3 gap-2 items-stretch rounded-lg border border-cede-border-container bg-cede-bg-component p-1 text-cede-text-secondary",
      underline: "pt-2 pb-2 justify-start h-full items-end gap-6 bg-transparent",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & VariantProps<typeof tabsListVariants>
>(({ className, variant, ...props }, ref) => (
  <TabsPrimitive.List ref={ref} className={cn(tabsListVariants({ variant }), className)} {...props} />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const tabsTriggerVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cede-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "rounded-sm px-3 py-1.5 ring-offset-background data-[state=active]:text-cede-primary text-cede-text-secondary hover:text-cede-text-primary",
        underline:
          "h-full items-center font-semibold border-b-2 data-[state=active]:text-cede-primary data-[state=active]:border-cede-primary text-cede-text-secondary border-transparent hover:text-white px-0",
        pill: "relative z-base gap-1.5 rounded-full px-4 py-1.5 transition-colors duration-300 data-[state=active]:text-cede-primary text-cede-text-secondary hover:text-cede-text-primary",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & VariantProps<typeof tabsTriggerVariants>
>(({ className, variant, ...props }, ref) => (
  <TabsPrimitive.Trigger ref={ref} className={cn(tabsTriggerVariants({ variant }), className)} {...props} />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      // Tab switches happen often — 180ms feels snappier than the original 300ms.
      "mt-2 animate-in fade-in slide-in-from-bottom-1 duration-200 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cede-primary focus-visible:ring-offset-2",
      className,
    )}
    style={{ animationTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)" }}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

// ─── Pill Tabs: animated sliding indicator ───

const PillTabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, children, ...props }, ref) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const mergedRef = useMergeRefs(containerRef, ref);
  const [pill, setPill] = React.useState<{ left: number; width: number } | null>(null);

  const measure = React.useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const active = container.querySelector<HTMLElement>("[data-state='active']");
    if (!active) return;
    const cRect = container.getBoundingClientRect();
    const aRect = active.getBoundingClientRect();
    setPill({ left: aRect.left - cRect.left, width: aRect.width });
  }, []);

  // Measure on mount
  React.useLayoutEffect(() => {
    measure();
  }, [measure]);

  React.useEffect(() => {
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [measure]);

  // Observe DOM mutations (Radix updates data-state attribute on active change)
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new MutationObserver(measure);
    observer.observe(container, { attributes: true, subtree: true, attributeFilter: ["data-state"] });
    return () => observer.disconnect();
  }, [measure]);

  return (
    <TabsPrimitive.List
      ref={mergedRef}
      className={cn(
        "relative inline-flex items-center gap-0.5 rounded-full border border-cede-border-container/40 bg-cede-bg-component/30 p-1",
        className,
      )}
      {...props}
    >
      {pill && (
        <div
          aria-hidden
          className="absolute top-1 bottom-1 rounded-full bg-cede-primary/15"
          style={{
            left: pill.left,
            width: pill.width,
            transition: "left 400ms cubic-bezier(0.16, 1, 0.3, 1), width 400ms cubic-bezier(0.16, 1, 0.3, 1)",
          }}
        />
      )}
      {children}
    </TabsPrimitive.List>
  );
});
PillTabsList.displayName = "PillTabsList";

const PillTabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      // 200ms enter — tab switches happen often, longer durations feel sluggish.
      "animate-in fade-in slide-in-from-bottom-1 duration-200 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cede-primary focus-visible:ring-offset-2",
      className,
    )}
    style={{ animationTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)" }}
    {...props}
  />
));
PillTabsContent.displayName = "PillTabsContent";

// Scrollable Tabs with Arrows
interface ScrollableTabsProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root> {
  children: React.ReactNode;
  containerClassName?: string;
}

const ScrollableTabs = React.forwardRef<React.ElementRef<typeof TabsPrimitive.Root>, ScrollableTabsProps>(
  ({ children, containerClassName, ...props }, ref) => {
    const scrollContainerRef = React.useRef<HTMLDivElement>(null);
    const [showLeftArrow, setShowLeftArrow] = React.useState(false);
    const [showRightArrow, setShowRightArrow] = React.useState(false);

    // Update arrow visibility based on scroll position
    const updateArrowVisibility = React.useCallback(() => {
      const container = scrollContainerRef.current;
      if (!container) return;

      const { scrollLeft, scrollWidth, clientWidth } = container;
      setShowLeftArrow(scrollLeft > 10);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }, []);

    // Auto-scroll to active tab when value changes
    React.useEffect(() => {
      const container = scrollContainerRef.current;
      if (!container) return;

      // Add a small delay to ensure DOM is updated
      const timeout = setTimeout(() => {
        const activeTab = container.querySelector('[data-state="active"]') as HTMLElement;
        if (activeTab) {
          const containerWidth = container.offsetWidth;
          const tabOffsetLeft = activeTab.offsetLeft;
          const tabWidth = activeTab.offsetWidth;

          // Calculate the scroll position to center the active tab
          const scrollPosition = tabOffsetLeft - containerWidth / 2 + tabWidth / 2;

          container.scrollTo({
            left: scrollPosition,
            behavior: "smooth",
          });
        }
      }, 50);

      return () => clearTimeout(timeout);
    }, [props.value]);

    // Check arrow visibility on mount and when scrolling
    React.useEffect(() => {
      updateArrowVisibility();
      const container = scrollContainerRef.current;
      if (container) {
        container.addEventListener("scroll", updateArrowVisibility, { passive: true });
        // Also check on resize
        const resizeObserver = new ResizeObserver(updateArrowVisibility);
        resizeObserver.observe(container);

        return () => {
          container.removeEventListener("scroll", updateArrowVisibility);
          resizeObserver.disconnect();
        };
      }
      return undefined;
    }, [updateArrowVisibility]);

    // Scroll functions
    const scrollLeft = () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollBy({ left: -150, behavior: "smooth" });
      }
    };

    const scrollRight = () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollBy({ left: 150, behavior: "smooth" });
      }
    };

    const tabBarHeight = "3.75rem";
    const arrowGradientLeft =
      "linear-gradient(to right, rgba(18, 27, 38, 0.98) 0%, rgba(18, 27, 38, 0.95) 30%, rgba(18, 27, 38, 0.7) 60%, rgba(18, 27, 38, 0.3) 85%, transparent 100%)";
    const arrowGradientRight =
      "linear-gradient(to left, rgba(18, 27, 38, 0.98) 0%, rgba(18, 27, 38, 0.95) 30%, rgba(18, 27, 38, 0.7) 60%, rgba(18, 27, 38, 0.3) 85%, transparent 100%)";

    return (
      <TabsPrimitive.Root ref={ref} {...props}>
        <div className={cn("relative flex items-center", containerClassName)} style={{ height: tabBarHeight }}>
          {/* Scrollable Tab Container */}
          <div
            ref={scrollContainerRef}
            className="flex h-full w-full items-end overflow-x-auto overflow-y-hidden px-8 py-3 transition-all [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [&::-webkit-scrollbar]:w-0"
          >
            {children}
          </div>

          {/* Left gradient */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 left-0 w-12 transition-opacity duration-150"
            style={{
              background: arrowGradientLeft,
              opacity: showLeftArrow ? 1 : 0,
            }}
          />
          {/* Right gradient */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 right-0 w-12 transition-opacity duration-150"
            style={{
              background: arrowGradientRight,
              opacity: showRightArrow ? 1 : 0,
            }}
          />

          {/* Left Arrow */}
          <button
            onClick={scrollLeft}
            type="button"
            aria-label="Scroll left"
            disabled={!showLeftArrow}
            className={cn(
              "absolute left-2 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-white transition-opacity duration-150 hover:text-cede-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cede-primary focus-visible:ring-offset-2 focus-visible:ring-offset-cede-bg-drawer",
              showLeftArrow ? "opacity-100" : "opacity-0 pointer-events-none",
            )}
          >
            <ChevronLeft className="h-5 w-5" />
          </button>

          {/* Right Arrow */}
          <button
            onClick={scrollRight}
            type="button"
            aria-label="Scroll right"
            disabled={!showRightArrow}
            className={cn(
              "absolute right-2 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-white transition-opacity duration-150 hover:text-cede-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cede-primary focus-visible:ring-offset-2 focus-visible:ring-offset-cede-bg-drawer",
              showRightArrow ? "opacity-100" : "opacity-0 pointer-events-none",
            )}
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </TabsPrimitive.Root>
    );
  },
);
ScrollableTabs.displayName = "ScrollableTabs";

export { Tabs, TabsList, TabsTrigger, TabsContent, PillTabsList, PillTabsContent, ScrollableTabs };
